import { useState } from "react";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";

interface Props {
  selected: string[];
  onChange: (ids: string[]) => void;
}

/**
 * Invitee picker for meetings. Unlike the Friday picker it lists ALL active
 * contractors (decision: contractors can be invited to meetings regardless of
 * Friday access).
 */
export function UserMultiSelect({ selected, onChange }: Props) {
  const [search, setSearch] = useState("");
  const { data: rawUsers = [] } = useInternalUsers({ includeContractors: true });
  const users = [...rawUsers].sort((a, b) =>
    a.full_name.localeCompare(b.full_name, "pl"),
  );

  const visible = search.trim()
    ? users.filter((u) => u.full_name.toLowerCase().includes(search.toLowerCase()))
    : users;

  const toggle = (id: string) => {
    onChange(
      selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id],
    );
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5">
        <span className="mr-1 text-sm text-[var(--color-text-secondary)]">
          Uczestnicy ({selected.length}):
        </span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj osoby…"
          className="w-44 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2.5 py-1 text-xs text-[var(--color-text-primary)] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
        />
      </div>
      <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto pr-1">
        {visible.map((u) => {
          const active = selected.includes(u.id);
          const isContractor = u.role === "contractor";
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => toggle(u.id)}
              title={u.full_name}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                active
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
              }`}
            >
              <UserAvatar
                userId={u.id}
                fullName={u.full_name}
                profilePictureUrl={u.profile_picture_url}
                size="xs"
              />
              {u.full_name}
              {isContractor && (
                <span
                  className={`rounded px-1 text-[10px] ${
                    active ? "bg-white/20" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
                  }`}
                >
                  podwyk.
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
